import { Table } from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Customer } from 'src/front/api/apiInstance';
import { AccountInfoData, ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { InfoLabel, InfoRow, Separator, Title } from 'src/front/components/FormStyles';
import { BREAK_POINTS } from 'src/front/constant';
import { useCustomerSearchState } from 'src/front/contexts/ViewCreditsContext';
import styled from 'styled-components';
import AccountSearchForm, { searchDefaultValues, SearchFormData } from './AccountSearchForm';

export const TableWrapper = styled.div`
  width: auto;

  @media screen and (min-width: ${BREAK_POINTS.MIN.LARGE_2X}) {
    width: 1070px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_2X}) and (min-width: ${BREAK_POINTS.MIN
      .LARGE_1X}) {
    width: 970px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) and (min-width: ${BREAK_POINTS.MIN
      .TABLET}) {
    width: 870px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    width: 600px;
    overflow-x: scroll;
  }
`;

const tableHeaders = [
  'allAccountObservation_accountNumber_title',
  'allAccountObservation_accountType_title',
  'allAccountObservation_balance_title',
  'allAccountObservation_currency_title',
  'allAccountObservation_branchCode_title',
  'allAccountObservation_openingDate_title',
  'allAccountObservation_managerName_title',
  'allAccountObservation_closingDate_title',
];

const AllAccountObservationList: FC = () => {
  const { t } = useTranslation();
  const accountTypes = usePRM(ReferenceDataType.ACCOUNTOBSERVATIONTYPE);
  const branches = usePRM(ReferenceDataType.BRANCH);
  const currencies = usePRM(ReferenceDataType.CURRENCY);
  const accountMasks = usePRM(ReferenceDataType.ACCOUNTMASK)
    .map((accountMask) => ({
      value: accountMask.value,
      label: accountMask.value,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

  const { customerId, customerSearchValues } = useCustomerSearchState();
  const [hasAccountNextPage, setHasAccountNextPage] = useState<boolean>(false);
  const [accountList, setAccountList] = useState<AccountInfoData[]>([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [accountPage, setAccountPage] = useState<number>(1);
  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(searchDefaultValues);
  const allAccountList = useCallback(
    async (
      pageNumber = accountPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ) => {
      setAccountsLoading(true);

      const { data } = await Customer.getAllAccountObservation({
        accountNumber: formValues.accountNumber,
        accountState: formValues.accountState?.value,
        accountType: formValues.accountType?.value,
        branchCode: formValues.branch?.value,
        currency: formValues.currency?.value,
        accountMask: formValues.accountMask?.value,
        channelCode: formValues.channel?.value,
        customerId: customerId || '',
        // accountManager: formValues.accountManager,
        pageNumber,
        refresh: !pageNumber || forceRefresh,
        rowCountPerPage: 10,
      });
      if (data.result === 'OK') {
        setAccountList(data.accountInfoList);
        setHasAccountNextPage(data.hasNextPage);
        setAccountPage(pageNumber || 1);
      }
      setAccountsLoading(false);
    },
    [accountPage, searchFormValues, customerId]
  );

  // useEffect(() => {
  //   allAccountList(accountPage, true);
  // }, [accountPage, allAccountList]);

  const tableAccountList = accountList.map((account) => ({
    id: account.accountNumber,
    columns: [
      account.accountNumber,
      accountTypes.find((item) => {
        return item.value === account.accountType;
      })?.label || account.accountType,
      account.balance,
      account.currency,
      branches.find((item) => {
        return item.value === account.branchCode;
      })?.label || account.branchCode,
      account.managerName || '',
      account.openingDate,
      account.closingDate || '',
    ],
  }));

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    await allAccountList(1, true, formData);
  };
  return (
    <>
      <Title>
        <Trans i18nKey="general_components_subMenu_allAccObservation_label" />
      </Title>
      <Separator />
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_components_clientName_text" />:
        </InfoLabel>
        {`${customerSearchValues?.customerName} ${customerSearchValues?.customerSecondName} ${customerSearchValues?.customerSurname}`}
      </InfoRow>
      <Separator />
      <AccountSearchForm
        accountMasks={accountMasks}
        accountTypes={accountTypes}
        branches={branches}
        currencies={currencies}
        formValues={searchFormValues}
        onSubmit={onSubmit}
      />
      <Separator />
      <TableWrapper>
        <Table
          emptyTableText={t('general_components_notFoundAccount_errorMessage')}
          hasNextPage={hasAccountNextPage}
          headers={tableHeaders.map((head) => t(head))}
          items={tableAccountList}
          loading={accountsLoading}
          loadPageItems={allAccountList}
          pageNumber={accountPage}
          // tableWidth={TABLE_WIDTHS.DESKTOP_2X}
          // widths={['20', '10', '20', '20', '15', '15']}
          clearItemOnPageChange
        />
      </TableWrapper>
    </>
  );
};
export default AllAccountObservationList;
